import React from "react";
import { Link } from "react-router-dom";
import { useLanguage, pick } from "@/lib/lang";
import { t } from "@/lib/content";
import { IMG } from "@/lib/assets";
import { PORTRAITS } from "@/lib/portraits";
import { TESTIDS } from "@/constants/testIds";
import Reveal from "@/components/ui-brand/Reveal";
import PeonyMark from "@/components/ui-brand/PeonyMark";
import PhotoPlaceholder from "@/components/ui-brand/PhotoPlaceholder";

const Chapter = ({ index, label, body, align = "left", lang, decor }) => {
  const text = pick(body, lang).split("\n\n");
  return (
    <Reveal>
      <div
        data-testid={TESTIDS.aboutChapter(index)}
        className={`grid grid-cols-12 gap-6 lg:gap-12 py-20 md:py-28 border-t border-warmth/40 ${
          align === "right" ? "lg:[&>*:first-child]:col-start-7" : ""
        }`}
      >
        <div className="col-span-12 lg:col-span-3">
          <p className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-atmosphere/80">
            {label}
          </p>
          {decor}
        </div>
        <div className="col-span-12 lg:col-span-8 lg:col-start-5">
          {text.map((para, i) => (
            <p
              key={i}
              className={`font-display ${i === 0 ? "text-3xl md:text-4xl lg:text-[2.5rem] leading-[1.18]" : "text-2xl md:text-3xl leading-snug"} text-depth mt-${i === 0 ? "0" : "8"} max-w-3xl ${i === text.length - 1 ? "italic-serif text-atmosphere" : ""}`}
              style={i > 0 ? { marginTop: "1.5rem" } : {}}
            >
              {para}
            </p>
          ))}
        </div>
      </div>
    </Reveal>
  );
};

const AboutPage = () => {
  const { lang } = useLanguage();

  return (
    <>
      {/* HERO */}
      <section
        data-testid={TESTIDS.aboutHero}
        className="relative px-6 md:px-12 lg:px-24 pt-6 md:pt-12 pb-20 md:pb-32 overflow-hidden"
      >
        <PeonyMark
          size={720}
          opacity={0.05}
          className="absolute -left-32 -bottom-32 pointer-events-none"
        />

        <div className="grid grid-cols-12 gap-6 lg:gap-12">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <p className="eyebrow">{pick(t.about.eyebrow, lang)}</p>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="headline-hero mt-10">
                {pick(t.about.title_line_1, lang)}
                <br />
                <span className="italic-serif text-atmosphere">
                  {pick(t.about.title_line_2, lang)}
                </span>
              </h1>
            </Reveal>
            <Reveal delay={400}>
              <p className="body-lux-lg max-w-xl mt-12">{pick(t.about.intro, lang)}</p>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <Reveal>
              <PhotoPlaceholder
                testId="about-hero-portrait"
                src={PORTRAITS.aboutHero.src}
                alt={PORTRAITS.aboutHero.alt}
                caption={PORTRAITS.aboutHero.caption}
                label={PORTRAITS.aboutHero.label}
                aspect={PORTRAITS.aboutHero.aspect}
                tone={PORTRAITS.aboutHero.tone}
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* CHAPTERS */}
      <section className="relative px-6 md:px-12 lg:px-24 pb-20">
        <Chapter
          index={1}
          label={pick(t.about.chapter1_label, lang)}
          body={t.about.chapter1}
          lang={lang}
        />

        <Chapter
          index={2}
          label={pick(t.about.chapter2_label, lang)}
          body={t.about.chapter2}
          lang={lang}
        />

        {/* Visual breath – sabbatical map */}
        <Reveal>
          <div className="py-20 md:py-28 border-t border-warmth/40">
            <div className="grid grid-cols-12 gap-6 lg:gap-12 items-end">
              <div className="col-span-12 lg:col-span-5">
                <p className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-atmosphere">
                  Sabbatical · 6 months
                </p>
                <h3 className="font-display italic text-3xl md:text-4xl mt-6 text-depth">
                  Bali — Aotearoa — Rarotonga
                </h3>
                <p className="body-lux mt-6">
                  {lang === "de"
                    ? "Drei Inseln. Sechs Monate. Ein Beweis, dass es auch anders geht."
                    : "Three islands. Six months. A proof that it can be different."}
                </p>
              </div>
              <div className="col-span-12 lg:col-span-7">
                <div className="grid grid-cols-3 gap-3 md:gap-6">
                  {[
                    PORTRAITS.sabbaticalBali,
                    PORTRAITS.sabbaticalAotearoa,
                    PORTRAITS.sabbaticalRarotonga,
                  ].map((portrait, i) => (
                    <PhotoPlaceholder
                      key={portrait.caption}
                      testId={`about-sabbatical-${i}`}
                      src={portrait.src}
                      alt={portrait.alt}
                      caption={portrait.caption}
                      label={`0${i + 1}`}
                      aspect={portrait.aspect}
                      tone={portrait.tone}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <Chapter
          index={3}
          label={pick(t.about.chapter3_label, lang)}
          body={t.about.chapter3}
          lang={lang}
          align="right"
        />

        {/* WORTKANON — three monumental words */}
        <Reveal>
          <div
            data-testid="about-wortkanon"
            className="relative py-24 md:py-36 border-t border-warmth/40 overflow-hidden"
          >
            <PeonyMark
              size={680}
              opacity={0.05}
              className="absolute -right-32 -top-10 pointer-events-none"
            />
            <div className="grid grid-cols-12 gap-6 lg:gap-12">
              <div className="col-span-12 lg:col-span-3">
                <p className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-atmosphere/80">
                  {lang === "de" ? "Wortkanon" : "Word canon"}
                </p>
                <p className="font-display italic text-xl text-atmosphere mt-6 max-w-xs">
                  {lang === "de"
                    ? "Drei Worte, die geblieben sind."
                    : "Three words that stayed."}
                </p>
              </div>

              <div className="col-span-12 lg:col-span-9">
                <ul className="space-y-3 md:space-y-5">
                  {[
                    { de: "Abenteuer", en: "Adventure", num: "I" },
                    { de: "Freiheit", en: "Freedom", num: "II" },
                    { de: "Lebendigkeit", en: "Aliveness", num: "III" },
                  ].map((word, i) => (
                    <li
                      key={word.de}
                      className="group flex items-baseline gap-6 md:gap-10 border-b border-warmth/40 pb-3 md:pb-5"
                    >
                      <span className="font-mono text-[0.62rem] tracking-[0.32em] uppercase text-atmosphere/70 shrink-0 w-8">
                        {word.num}
                      </span>
                      <span className="font-display italic text-[clamp(2.6rem,8vw,7rem)] leading-[0.95] text-depth tracking-tight">
                        {pick(word, lang)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Photo strip – "today" — REMOVED per user request */}
      </section>

      {/* Closing */}
      <section className="relative bg-depth text-canvas px-6 md:px-12 lg:px-24 py-28 md:py-40 overflow-hidden">
        <PeonyMark size={780} color="#FAF7F2" opacity={0.06} className="absolute -right-40 -top-32" />
        <div className="relative grid grid-cols-12 gap-6 lg:gap-12">
          <div className="col-span-12 lg:col-span-9">
            <Reveal>
              <p className="eyebrow-light">— {lang === "de" ? "Heute" : "Today"}</p>
              <p className="font-display italic text-[clamp(2.4rem,5.5vw,5.4rem)] leading-[1.04] mt-8 max-w-4xl">
                {lang === "de"
                  ? "Heute arbeite ich digital. Von dort, wo ich gerade bin. Mit vollem Herzen."
                  : "Today I work digitally. From wherever I am. With my whole heart."}
              </p>
            </Reveal>
            <Reveal delay={300}>
              <Link to="/kontakt" data-testid={TESTIDS.aboutCta} className="btn-light mt-16 w-fit">
                {pick(t.about.cta, lang)}
                <span aria-hidden>↗</span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
